import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import type { DemoSwitchUser, SessionSummary, ClassSummary, StudentSummary, OrgMember } from '../api/types'
import { useDemoData } from './useDemoData'

type ActiveRole = 'ADMIN' | 'ORG_MANAGER' | 'TEACHER'

interface DemoContextValue {
  activeUser: DemoSwitchUser
  activeRole: ActiveRole
  demoUsersList: DemoSwitchUser[]
  switchUser: (userId: number) => void

  // Mutable state for simulating CRUD
  exams: SessionSummary[]
  addExam: (exam: SessionSummary) => void
  deleteExam: (id: number) => void

  classes: ClassSummary[]
  addClass: (cls: ClassSummary) => void

  studentsByClass: Record<number, StudentSummary[]>
  addStudent: (classId: number, student: StudentSummary) => void

  members: OrgMember[]
  addMember: (member: OrgMember) => void
  removeMember: (id: number) => void
}

const DemoContext = createContext<DemoContextValue | null>(null)

export function useDemoContext(): DemoContextValue {
  const ctx = useContext(DemoContext)
  if (!ctx) throw new Error('useDemoContext must be used within DemoProvider')
  return ctx
}

export function DemoProvider({ children }: { children: ReactNode }) {
  const data = useDemoData()
  const [activeUserId, setActiveUserId] = useState(() => data.users[0]?.id ?? 1)
  const [exams, setExams] = useState<SessionSummary[]>(data.exams)
  const [classes, setClasses] = useState<ClassSummary[]>(data.classes)
  const [studentsByClass, setStudentsByClass] = useState<Record<number, StudentSummary[]>>({
    ...data.studentsByClass,
  })
  const [members, setMembers] = useState<OrgMember[]>(data.members)

  // Re-seed the mutable demo state when the UI language (and therefore the
  // fixture set) changes. Language switching is rare and demo CRUD is
  // throwaway, so resetting is acceptable.
  useEffect(() => {
    setActiveUserId((prev) => (data.users.some((u) => u.id === prev) ? prev : data.users[0]?.id ?? 1))
    setExams(data.exams)
    setClasses(data.classes)
    setStudentsByClass({ ...data.studentsByClass })
    setMembers(data.members)
  }, [data])

  const activeUser = data.users.find((u) => u.id === activeUserId) ?? data.users[0]
  const activeRole = activeUser.user_role as ActiveRole

  const switchUser = useCallback((userId: number) => {
    setActiveUserId(userId)
  }, [])

  const addExam = useCallback((exam: SessionSummary) => {
    setExams((prev) => [exam, ...prev])
  }, [])

  const deleteExam = useCallback((id: number) => {
    setExams((prev) => prev.filter((e) => e.id !== id))
  }, [])

  const addClass = useCallback((cls: ClassSummary) => {
    setClasses((prev) => [cls, ...prev])
    setStudentsByClass((prev) => ({ ...prev, [cls.id]: [] }))
  }, [])

  const addStudent = useCallback((classId: number, student: StudentSummary) => {
    setStudentsByClass((prev) => ({
      ...prev,
      [classId]: [...(prev[classId] || []), student],
    }))
  }, [])

  const addMemberFn = useCallback((member: OrgMember) => {
    setMembers((prev) => [member, ...prev.filter((m) => m.id !== member.id)])
  }, [])

  const removeMemberFn = useCallback((id: number) => {
    setMembers((prev) => prev.filter((m) => m.id !== id))
  }, [])

  return (
    <DemoContext.Provider
      value={{
        activeUser,
        activeRole,
        demoUsersList: data.users,
        switchUser,
        exams,
        addExam,
        deleteExam,
        classes,
        addClass,
        studentsByClass,
        addStudent,
        members,
        addMember: addMemberFn,
        removeMember: removeMemberFn,
      }}
    >
      {children}
    </DemoContext.Provider>
  )
}
